// client/src/coms/views/chatview_parts/StickerPicker.js
// Composer sticker picker: favourites and every pack usable in this conversation (the server decides which:
// GET /stickers/usable), plus this device's local packs. This server's packs need Send stickers, personal
// and local packs Use external stickers. onPick(sticker) sends it (the composer relays local stickers).
import React, { useEffect, useMemo, useState } from 'react';
import { comsApiDelete, comsApiPut } from '../../comsApi';
import { stickerResolver } from '../../customMedia';
import { invalidateSuggestionPacks, loadSuggestionPacks } from '../../stickers/stickerSuggest';
import { updateLocalSticker } from '../../stickers/localStickerStore';

export default function StickerPicker({ serverKey, onPick, onClose, onManage, canSendServer = true, canSendExternal = true }) {
  const [packs, setPacks] = useState(null);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('fav');

  useEffect(() => {
    let alive = true;
    loadSuggestionPacks({ serverKey, force: true })
      .then((usable) => {
        if (!alive) return;
        const list = usable.filter((p) => (p.via === 'server' ? canSendServer : canSendExternal));
        setPacks(list);
        stickerResolver.prime(list.filter((p) => !p.local).flatMap((p) => p.stickers || []));
        if (!list.some((p) => (p.stickers || []).some((s) => s.favorite)) && list.length) setTab(String(list[0].id));
      })
      .catch(() => alive && setErr('Could not load stickers.'));
    return () => {
      alive = false;
    };
  }, [serverKey, canSendServer, canSendExternal]);

  const all = useMemo(() => (packs || []).flatMap((p) => (p.stickers || []).map((s) => ({ ...s, packName: p.name }))), [packs]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) return all.filter((s) => s.name.toLowerCase().includes(q) || s.packName.toLowerCase().includes(q) || (s.keywords || []).some((k) => k.startsWith(q)));
    if (tab === 'fav') return all.filter((s) => s.favorite);
    const pack = (packs || []).find((p) => String(p.id) === tab);
    return pack ? pack.stickers || [] : [];
  }, [all, packs, query, tab]);

  async function toggleFavorite(s) {
    try {
      if (s.local) {
        await updateLocalSticker(s.localId, { favorite: !s.favorite }, { silent: true });
        invalidateSuggestionPacks();
      } else if (s.favorite) await comsApiDelete(`/stickers/stickers/${s.id}/favorite`);
      else await comsApiPut(`/stickers/stickers/${s.id}/favorite`, {});
      setPacks((list) => (list || []).map((p) => ({ ...p, stickers: (p.stickers || []).map((x) => (x.id === s.id ? { ...x, favorite: !s.favorite } : x)) })));
    } catch {}
  }

  const tabBtn = (key, label, icon) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      title={label}
      style={{
        border: 0,
        borderRadius: 8,
        background: tab === key ? 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))' : 'transparent',
        color: 'var(--vx-text, #fff)',
        padding: 4,
        width: 36,
        height: 36,
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {icon ? <img src={icon} alt="" style={{ width: 26, height: 26, objectFit: 'contain' }} /> : <span style={{ fontSize: 16 }}>{label.slice(0, 1)}</span>}
    </button>
  );

  return (
    <div style={{ width: 'min(380px, calc(100vw - 16px))', height: 420, display: 'flex', flexDirection: 'column', background: 'var(--vxs-3, #1b1c1f)', color: 'var(--vx-text, #fff)' }}>
      <div style={{ display: 'flex', gap: 6, padding: 8, alignItems: 'center' }}>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search stickers"
          style={{ flex: 1, background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '6px 8px' }}
        />
        {typeof onManage === 'function' ? (
          <button type="button" className="coms-mini-btn tiny" onClick={onManage}>
            Manage
          </button>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 4, padding: '0 8px 6px', overflowX: 'auto' }}>
        {tabBtn('fav', 'Favourites', '')}
        {(packs || []).map((p) => tabBtn(String(p.id), p.name, p.icon_url || (p.stickers && p.stickers[0] && p.stickers[0].url)))}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, alignContent: 'start' }}>
        {err ? <div style={{ gridColumn: '1 / -1', fontSize: 13, color: '#ff8a8a' }}>{err}</div> : null}
        {packs && !all.length ? (
          <div style={{ gridColumn: '1 / -1', fontSize: 13, opacity: 0.75 }}>No stickers yet. Use Manage to create a pack or install one shared with you.</div>
        ) : null}
        {packs && all.length && !shown.length ? (
          <div style={{ gridColumn: '1 / -1', fontSize: 13, opacity: 0.75 }}>{query ? 'No matching stickers.' : 'Nothing here yet. Star a sticker to add it to favourites.'}</div>
        ) : null}
        {shown.map((s) => (
          <div key={s.id} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => {
                onPick(s);
                if (typeof onClose === 'function') onClose();
              }}
              title={s.name}
              style={{ width: '100%', aspectRatio: '1', border: 0, borderRadius: 8, background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))', padding: 4, cursor: 'pointer' }}
            >
              <img src={s.url} alt={s.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </button>
            <button
              type="button"
              onClick={() => toggleFavorite(s)}
              aria-label={s.favorite ? 'Remove from favourites' : 'Add to favourites'}
              style={{ position: 'absolute', top: 2, right: 2, border: 0, background: 'transparent', color: s.favorite ? '#fbbf24' : 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.45 * var(--vx-ov, 1)))', cursor: 'pointer', fontSize: 14 }}
            >
              ★
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
